import * as Api from '@ViewModels';
import { Button } from '@WebComponents/Button';
import { Checkbox } from '@WebComponents/Checkbox';
import { DangerIcon } from '@WebComponents/svgs/icons/DangerIcon';
import { SuccessCheckIcon } from '@WebComponents/svgs/icons/SuccessCheckIcon';
import { css } from 'aphrodite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
	invalidateIntegrationQuery,
	usePostEclipseTenantsPreviewMutation,
	usePostExternalIntegrationConfigurationMutation,
} from '../../../../../../../../../queries';
import { LoadingSpinner } from '../../../../../../../../../web/components/LoadingSpinner';
import { white } from '../../../../../../../../../web/styles/colors';
import { SettingGroup } from '../SettingGroup';
import { styleSheet } from './styles';

enum SetupState {
	Configured = 'configured',
	Input = 'input',
	Preview = 'preview',
}

// Parse agencyIds from textarea input
const parseAgencyIds = (input: string): string[] => {
	return input
		.split(/[\n,]+/)
		.map(id => id.trim())
		.filter(id => id.length > 0);
};

interface IEclipseTenantSetupProps {
	impersonationContext: Api.IImpersonationContext;
	configuration: Api.IIntegrationConfiguration;
	onConfigurationSaved: () => void;
}

export const EclipseTenantSetup: React.FC<IEclipseTenantSetupProps> = ({
	impersonationContext,
	configuration,
	onConfigurationSaved,
}) => {
	const eclipseConfig = configuration as Api.IEclipseIntegrationConfiguration;

	const [setupState, setSetupState] = useState<SetupState>(SetupState.Configured);
	const [tenantIdInput, setTenantIdInput] = useState('');
	const [tenantsPreview, setTenantsPreview] = useState<Api.IEclipseTenantInfo[]>([]);
	const [syncAllAgencies, setSyncAllAgencies] = useState(true);
	const [agencyIdsInput, setAgencyIdsInput] = useState('');

	// Get current configuration values
	const currentTenantId = eclipseConfig?.tenantIds?.[0] || '';
	const currentSyncAllAgencies = eclipseConfig?.syncAllAgencies ?? true;
	const currentAgencyIds = useMemo(() => eclipseConfig?.agencyIds || [], [eclipseConfig?.agencyIds]);
	const isConfigured = !!currentTenantId;

	// Get only authorized agencies
	const authorizedAgencies = tenantsPreview.filter(tenant => tenant.isAuthorized);

	// Get validation errors from unauthorized agencies
	const validationErrors = tenantsPreview
		.filter(tenant => !tenant.isAuthorized && tenant.error)
		.map(tenant => `${tenant.id}: ${tenant.error}`);

	// Check if the current selection is valid
	const isSelectionValid = syncAllAgencies ? authorizedAgencies.length > 0 : parseAgencyIds(agencyIdsInput).length > 0;

	// Set initial state and reset form when entering Input state
	const hasInitialized = React.useRef(false);
	useEffect(() => {
		if (!hasInitialized.current) {
			hasInitialized.current = true;
			setSetupState(isConfigured ? SetupState.Configured : SetupState.Input);
		}
	}, [isConfigured]);

	useEffect(() => {
		if (setupState === SetupState.Input) {
			setTenantIdInput(currentTenantId);
			setSyncAllAgencies(currentSyncAllAgencies);
			setAgencyIdsInput(currentAgencyIds.join('\n'));
		}
	}, [setupState, currentTenantId, currentSyncAllAgencies, currentAgencyIds]);

	const tenantsPreviewMutation = usePostEclipseTenantsPreviewMutation({
		impersonationContext,
		onError: (error: Api.IOperationResultNoValue) => {
			toast.error(error.systemMessage || 'Failed to load agency details');
		},
	});

	const configMutation = usePostExternalIntegrationConfigurationMutation({
		impersonationContext,
		onError: (error: Api.IOperationResultNoValue) => {
			toast.error(error.systemMessage || 'Failed to save configuration');
		},
		onSuccess: () => {
			toast.success('Agency configuration saved successfully');
		},
	});

	const onEdit = useCallback(() => {
		setSetupState(SetupState.Input);
		setTenantsPreview([]);
	}, []);

	const onPreview = useCallback(async () => {
		if (!tenantIdInput.trim()) {
			toast.error('Please enter a Tenant ID');
			return;
		}

		const parsedAgencyIds = syncAllAgencies ? [] : parseAgencyIds(agencyIdsInput);

		try {
			const tenantsResult = await tenantsPreviewMutation.mutateAsync({
				tenantIds: [tenantIdInput.trim()],
				syncAllAgencies,
				agencyIds: parsedAgencyIds,
			});

			setTenantsPreview(tenantsResult.tenants);
			setSetupState(SetupState.Preview);
		} catch {
			// Error handled in mutation onError
		}
	}, [tenantIdInput, syncAllAgencies, agencyIdsInput, tenantsPreviewMutation]);

	const onConfirm = useCallback(async () => {
		const parsedAgencyIds = syncAllAgencies ? [] : parseAgencyIds(agencyIdsInput);

		try {
			await configMutation.mutateAsync({
				integrationProvider: Api.IntegrationProvider.Eclipse,
				configuration: {
					tenantIds: [tenantIdInput.trim()],
					syncAllAgencies,
					agencyIds: parsedAgencyIds,
				} as Partial<Api.IEclipseIntegrationConfiguration>,
			});
			invalidateIntegrationQuery({
				impersonationContext,
				provider: Api.IntegrationProvider.Eclipse,
			});
			onConfigurationSaved();
			setSetupState(SetupState.Configured);
			setTenantsPreview([]);
		} catch {
			// Error handled in mutation onError
		}
	}, [tenantIdInput, syncAllAgencies, agencyIdsInput, configMutation, impersonationContext, onConfigurationSaved]);

	const onCancel = useCallback(() => {
		setSetupState(isConfigured ? SetupState.Configured : SetupState.Input);
		setTenantsPreview([]);
		setTenantIdInput(currentTenantId);
		setSyncAllAgencies(currentSyncAllAgencies);
		setAgencyIdsInput(currentAgencyIds.join('\n'));
	}, [isConfigured, currentTenantId, currentSyncAllAgencies, currentAgencyIds]);

	const isLoading = tenantsPreviewMutation.isPending || configMutation.isPending;

	const renderConfigured = () => (
		<div className={css(styleSheet.section)}>
			<p className={css(styleSheet.description)}>Your Eclipse integration is configured.</p>
			<div className={css(styleSheet.configuredSection)}>
				<div className={css(styleSheet.configuredLabel)}>Tenant ID:</div>
				<div className={css(styleSheet.configuredValue)}>{currentTenantId}</div>
			</div>
			<div className={css(styleSheet.configuredSection)}>
				<div className={css(styleSheet.configuredLabel)}>Agency Sync Mode:</div>
				<div className={css(styleSheet.configuredValue)}>
					{currentSyncAllAgencies ? 'Sync all agencies' : 'Sync specific agencies'}
				</div>
			</div>
			{!currentSyncAllAgencies && currentAgencyIds.length > 0 && (
				<div className={css(styleSheet.configuredSection)}>
					<div className={css(styleSheet.configuredLabel)}>Agency IDs:</div>
					<ul className={css(styleSheet.agencyList)}>
						{currentAgencyIds.map((id, index) => (
							<li key={index} className={css(styleSheet.agencyListItem, styleSheet.configuredValue)}>
								{id}
							</li>
						))}
					</ul>
				</div>
			)}
			<div className={css(styleSheet.buttonGroup)}>
				<Button kind='reverse' onClick={onEdit}>
					Edit
				</Button>
			</div>
		</div>
	);

	const renderInput = () => (
		<div className={css(styleSheet.section)}>
			<p className={css(styleSheet.description)}>
				Configure the Eclipse integration by entering your Tenant ID and selecting which agencies to sync.
			</p>

			<div className={css(styleSheet.formGroup)}>
				<label className={css(styleSheet.formLabel)} htmlFor='requirement-target-Eclipse-tenant'>
					Tenant ID
				</label>
				<input
					id='requirement-target-Eclipse-tenant'
					type='text'
					className={css(styleSheet.textInput)}
					value={tenantIdInput}
					onChange={e => setTenantIdInput(e.target.value)}
					placeholder='Enter Tenant ID'
					disabled={isLoading}
				/>
			</div>

			<div className={css(styleSheet.checkboxRow)}>
				<Checkbox
					checked={syncAllAgencies}
					onChange={e => setSyncAllAgencies((e.target as HTMLInputElement).checked)}
					disabled={isLoading}
				/>
				<span
					className={css(styleSheet.checkboxLabel)}
					onClick={() => !isLoading && setSyncAllAgencies(!syncAllAgencies)}
				>
					Sync all agencies
				</span>
			</div>

			{!syncAllAgencies && (
				<div className={css(styleSheet.formGroup)}>
					<label className={css(styleSheet.formLabel)} htmlFor='requirement-target-Eclipse-agencies'>
						Agency IDs (one per line)
					</label>
					<textarea
						id='requirement-target-Eclipse-agencies'
						className={css(styleSheet.textArea)}
						value={agencyIdsInput}
						onChange={e => setAgencyIdsInput(e.target.value)}
						placeholder='Enter one Agency ID per line'
						rows={4}
						disabled={isLoading}
					/>
				</div>
			)}

			<div className={css(styleSheet.buttonGroup)}>
				<Button
					kind='primary'
					onClick={onPreview}
					disabled={isLoading || !tenantIdInput.trim() || (!syncAllAgencies && !agencyIdsInput.trim())}
				>
					{tenantsPreviewMutation.isPending ? 'Loading...' : 'Preview'}
				</Button>
				{isConfigured && (
					<Button kind='reverse' onClick={onCancel} disabled={isLoading}>
						Cancel
					</Button>
				)}
			</div>
		</div>
	);

	const renderPreview = () => {
		const parsedAgencyIds = parseAgencyIds(agencyIdsInput);

		return (
			<div className={css(styleSheet.section)}>
				<p className={css(styleSheet.description)}>
					Review the available agencies below. Click Confirm to save the configuration.
				</p>

				<div className={css(styleSheet.configuredSection)}>
					<div className={css(styleSheet.configuredLabel)}>Tenant ID:</div>
					<div className={css(styleSheet.configuredValue)}>{tenantIdInput}</div>
				</div>

				<div className={css(styleSheet.configuredSection)}>
					<div className={css(styleSheet.configuredLabel)}>Agencies to Sync:</div>
					{syncAllAgencies ? (
						<div className={css(styleSheet.configuredValue)}>All</div>
					) : (
						<ul className={css(styleSheet.agencyList)}>
							{parsedAgencyIds.map((id, index) => (
								<li key={index} className={css(styleSheet.agencyListItem, styleSheet.configuredValue)}>
									{id}
								</li>
							))}
						</ul>
					)}
				</div>

				{tenantsPreview.length === 0 ? (
					<div className={css(styleSheet.configuredSection)}>
						<div className={css(styleSheet.configuredLabel)}>Agencies Found:</div>
						<div className={css(styleSheet.configuredValue, styleSheet.statusNotAuthorized)}>
							No agencies found. Please check the Tenant ID and try again.
						</div>
					</div>
				) : (
					<>
						<div className={css(styleSheet.configuredSection)}>
							<div className={css(styleSheet.configuredLabel)}>Agencies Found:</div>
							{validationErrors.length > 0 ? (
								<div className={css(styleSheet.statusCell)}>
									<DangerIcon className={css(styleSheet.statusIcon)} backgroundColor={white} />
									<span className={css(styleSheet.statusNotAuthorized)}>
										{authorizedAgencies.length} of {tenantsPreview.length} authorized
									</span>
								</div>
							) : (
								<div className={css(styleSheet.statusCell)}>
									<SuccessCheckIcon className={css(styleSheet.statusIcon)} />
									<span className={css(styleSheet.statusAuthorized)}>
										{authorizedAgencies.length} {authorizedAgencies.length === 1 ? 'agency' : 'agencies'} authorized
									</span>
								</div>
							)}
						</div>

						{tenantsPreview.map((tenant, index) => (
							<div key={index} className={css(styleSheet.agencyCard)}>
								<div className={css(styleSheet.agencyCardHeader)}>
									<span className={css(styleSheet.agencyCardName)}>{tenant.name || tenant.id}</span>
									{tenant.isAuthorized ? (
										<div className={css(styleSheet.statusCell)}>
											<SuccessCheckIcon className={css(styleSheet.statusIcon)} />
											<span className={css(styleSheet.statusAuthorized)}>Authorized</span>
										</div>
									) : (
										<div className={css(styleSheet.statusCell)}>
											<DangerIcon className={css(styleSheet.statusIcon)} backgroundColor={white} />
											<span className={css(styleSheet.statusNotAuthorized)}>{tenant.error || 'Not Authorized'}</span>
										</div>
									)}
								</div>
								<div className={css(styleSheet.agencyCardDetails)}>
									{tenant.name && <div>ID: {tenant.id}</div>}
									{tenant.address && <div>{tenant.address}</div>}
								</div>
							</div>
						))}
					</>
				)}

				<div className={css(styleSheet.buttonGroup)}>
					<Button kind='primary' onClick={onConfirm} disabled={isLoading || !isSelectionValid}>
						{configMutation.isPending ? 'Saving...' : 'Confirm'}
					</Button>
					<Button kind='reverse' onClick={onCancel} disabled={isLoading}>
						Back
					</Button>
				</div>
			</div>
		);
	};

	return (
		<SettingGroup title='Agency Configuration'>
			{isLoading && setupState !== SetupState.Preview ? (
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner type='small' />
				</div>
			) : (
				<>
					{setupState === SetupState.Configured && renderConfigured()}
					{setupState === SetupState.Input && renderInput()}
					{setupState === SetupState.Preview && renderPreview()}
				</>
			)}
		</SettingGroup>
	);
};
